/**
 * useSGPCacheData — data layer for SGPCacheTab.
 * Extracted verbatim from components/SGPCacheTab.jsx (god-file decomposition).
 * Owns all fetch/generate/toggle state + effects + functions; returns the surface
 * the SGPCacheTab render + remaining helpers consume. Completeness machine-verified
 * by scripts/check-extraction.js.
 */

import { useState, useEffect, useMemo } from 'react';

export function useSGPCacheData() {
  const [fixtures, setFixtures] = useState([]);
  const [cachedSGPs, setCachedSGPs] = useState({});
  const [selectedLeagues, setSelectedLeagues] = useState([]); // Empty = show all
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [expandedFixtures, setExpandedFixtures] = useState({});
  const [expandedSGPs, setExpandedSGPs] = useState({});
  const [generationLogs, setGenerationLogs] = useState({});
  const [bulkProgress, setBulkProgress] = useState(null); // {processed, total, successful, failed, status}
  
  // ⭐ Section collapse state for date groups
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // ⭐ NEW: Timer and stats state
  const [countdown, setCountdown] = useState(null);
  const [sgpCacheStats, setSgpCacheStats] = useState(null);

  // ⭐ NEW: Automation health (getSgpHealth)
  const [sgpHealth, setSgpHealth] = useState(null);
  const [sgpHealthExpanded, setSgpHealthExpanded] = useState(false);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  // Dynamic leagues based on available fixtures
  const leagues = useMemo(() => {
    const uniqueLeagues = new Set();
    fixtures.forEach(fixture => {
      if (fixture.league) {
        uniqueLeagues.add(fixture.league.toLowerCase());
      }
    });
    
    const leagueOptions = [{ id: 'all', name: 'All Leagues' }];
    
    // Map league IDs to display names
    const leagueNames = {
      'nba': 'NBA',
      'nfl': 'NFL',
      'nhl': 'NHL',
      'mlb': 'MLB',
      'ncaab': 'NCAAB',
      'ncaaf': 'NCAAF',
      'mls': 'MLS',
      'epl': 'EPL',
      'uefa': 'UEFA'
    };
    
    Array.from(uniqueLeagues).sort().forEach(league => {
      leagueOptions.push({
        id: league,
        name: leagueNames[league] || league.toUpperCase()
      });
    });
    
    return leagueOptions;
  }, [fixtures]);
  
  useEffect(() => {
    fetchFixtures();
    fetchSGPCacheStats();
    fetchSgpHealth();
  }, []);
  
  // Fetch SGP status after fixtures are loaded
  useEffect(() => {
    if (fixtures.length > 0) {
      fetchAllCachedSGPs();
    }
  }, [fixtures.length]);
  
  // ⭐ Calculate countdown to next SGP auto-refresh.
  // Primary sports (basketball/hockey/baseball/football) run at :20 and :50.
  // Niche sports (tennis/combat/golf…) run at :27–:34.
  // Multi-leg append pass runs at :40–:43.
  // We show the nearest upcoming :20 or :50 as "next primary refresh".
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const m = now.getMinutes();
      let nextMinute;
      if (m < 20)      nextMinute = 20;
      else if (m < 50) nextMinute = 50;
      else             nextMinute = 20; // next hour

      const next = new Date(now);
      if (nextMinute === 20 && m >= 50) {
        next.setHours(now.getHours() + 1, 20, 0, 0);
      } else {
        next.setMinutes(nextMinute, 0, 0);
      }

      const diffMs = next - now;
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setCountdown(`${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // ⭐ Auto-refresh stats every 30 seconds while page is open
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      if (!loading && !generatingAll) {
        console.log('[SGPCache] Auto-refreshing stats...');
        fetchSGPCacheStats();
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(autoRefreshInterval);
  }, [loading, generatingAll]);
  
  // ⭐ Fetch REAL SGP cache stats (aggregate from actual cache, not last-run stats)
  const fetchSGPCacheStats = async () => {
    try {
      // Use new aggregate stats endpoint for REAL cache state
      const response = await fetch(`${functionsUrl}/getSGPCacheAggregateStats`);
      const data = await response.json();
      
      if (data.success && data.aggregate) {
        // Transform to expected format for UI
        setSgpCacheStats({
          success: true,
          aggregate: data.aggregate,
          stats: {
            total_fixtures: data.aggregate.total_fixtures_available,
            sgps_generated: data.aggregate.total_fixtures_cached,
            total_sgps: data.aggregate.total_sgps,
            by_league: data.by_league
          },
          uncached_sample: data.uncached_sample,
          timestamps: data.timestamps, // Now includes last_sgp_generation
          // Keep status field for UI compatibility
          status: data.aggregate.cache_coverage_percent === 100 ? 'complete' : 'partial'
        });
        console.log('[SGPCache] Aggregate stats loaded:', data.aggregate);
        return; // Success, don't fallback
      }
      throw new Error('No aggregate data in response');
    } catch (err) {
      console.error('[SGPCache] Failed to fetch aggregate stats:', err);
      // Fallback to progress endpoint if aggregate fails
      try {
        const fallbackResponse = await fetch(`${functionsUrl}/getSGPGenerationProgress`);
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.success) {
          // ⭐ Transform fallback data to include aggregate structure
          const totalFixtures = fallbackData.stats?.total_fixtures || fallbackData.total || 0;
          const cachedFixtures = fallbackData.stats?.sgps_generated || fallbackData.successful || 0;
          const totalSgps = fallbackData.stats?.total_sgps || fallbackData.totalSGPs || 0;
          
          setSgpCacheStats({
            ...fallbackData,
            aggregate: {
              total_fixtures_available: totalFixtures,
              total_fixtures_cached: cachedFixtures,
              total_fixtures_uncached: totalFixtures - cachedFixtures,
              cache_coverage_percent: totalFixtures > 0 ? Math.round((cachedFixtures / totalFixtures) * 100) : 0,
              total_sgps: totalSgps
            }
          });
          console.log('[SGPCache] Using fallback stats with calculated coverage');
        }
      } catch (fallbackErr) {
        console.error('[SGPCache] Fallback also failed:', fallbackErr);
      }
    }
  };
  
  // ⭐ NEW: Fetch SGP automation health
  const fetchSgpHealth = async () => {
    try {
      const res = await fetch(`${functionsUrl}/getSgpHealth`);
      const data = await res.json();
      if (data.success) setSgpHealth(data);
    } catch (e) {
      console.error('[SGPCache] getSgpHealth failed:', e);
    }
  };

  // Fetch all fixtures - now prioritizes fixtures with cached SGPs
  const fetchFixtures = async () => {
    try {
      setLoading(true);
      
      // ⭐ FIXED: Fetch fixtures that have cached SGPs directly
      // Increased limits to show all fixtures
      const [sgpResponse, oddsResponse] = await Promise.all([
        fetch(`${functionsUrl}/queryCachedSGPs?limit=200&max_age_hours=48`), // More fixtures, longer window
        fetch(`${functionsUrl}/getAllCachedFixtures?limit=300&has_odds=true`)
      ]);
      
      const sgpData = await sgpResponse.json();
      const oddsData = await oddsResponse.json();
      
      console.log('[SGPCache] Fetched from SGP cache:', sgpData.fixtures?.length || 0);
      console.log('[SGPCache] Fetched from odds cache:', oddsData.fixtures?.length || 0);
      
      // Build a map of fixtures with SGPs
      const fixturesMap = new Map();
      const threeHoursAgo = Date.now() - (3 * 60 * 60 * 1000);
      
      // Helper to check if fixture is in the past
      const isUpcoming = (fixture) => {
        const startDate = fixture.start_date;
        if (!startDate) return true; // Include if unknown
        
        let gameTime;
        if (startDate._seconds) {
          gameTime = startDate._seconds * 1000;
        } else {
          gameTime = new Date(startDate).getTime();
        }
        
        return gameTime > threeHoursAgo;
      };
      
      // Add fixtures from SGP cache first (these have confirmed SGPs)
      if (sgpData.success && sgpData.fixtures) {
        sgpData.fixtures.forEach(f => {
          if (!isUpcoming(f)) return; // ⭐ Filter out past fixtures
          
          const id = f.fixture_id || f.id;
          fixturesMap.set(id, {
            ...f,
            fixture_id: id,
            has_cached_sgps: true,
            sgp_count: f.stats?.total_sgps || 40
          });
        });
      }
      
      // Add fixtures from odds cache that pass SGP-ready filter (if not already added)
      if (oddsData.success && oddsData.fixtures) {
        oddsData.fixtures.forEach(f => {
          if (!isUpcoming(f)) return; // ⭐ Filter out past fixtures
          
          const id = f.fixture_id || f.id;
          const marketCount = f.total_markets || 0;
          const oddsCount = f.total_odds_available || 0;
          
          // Only add if passes filter AND not already from SGP cache
          if (!fixturesMap.has(id) && marketCount >= 10 && oddsCount >= 30) {
            fixturesMap.set(id, {
              ...f,
              fixture_id: id,
              has_cached_sgps: false
            });
          }
        });
      }
      
      const allFixtures = Array.from(fixturesMap.values());
      console.log('[SGPCache] Total unique fixtures (upcoming only):', allFixtures.length);
      
      setFixtures(allFixtures);
      
      // ⭐ Pre-populate cachedSGPs state from the SGP cache response
      if (sgpData.success && sgpData.fixtures) {
        const sgpStatus = {};
        sgpData.fixtures.forEach(f => {
          const id = f.fixture_id || f.id;
          sgpStatus[id] = {
            stats: f.stats || { total_sgps: 40 },
            generated_at: f.generated_at,
            has_sgps: true
          };
        });
        setCachedSGPs(prev => ({ ...prev, ...sgpStatus }));
        console.log('[SGPCache] Pre-populated SGP status for', Object.keys(sgpStatus).length, 'fixtures');
      }
    } catch (err) {
      console.error('Failed to fetch fixtures:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch all cached SGPs status (lightweight check for each fixture)
  const fetchAllCachedSGPs = async () => {
    try {
      // Fetch SGP status for all fixtures in parallel
      // This is a lightweight check to determine border styling
      const fixtureIds = fixtures.map(f => f.fixture_id || f.id);
      
      if (fixtureIds.length === 0) return;
      
      console.log('[SGPCache] Fetching SGP status for', fixtureIds.length, 'fixtures...');
      
      // Fetch in parallel (limited to 10 at a time to avoid overwhelming the server)
      const batchSize = 10;
      for (let i = 0; i < fixtureIds.length; i += batchSize) {
        const batch = fixtureIds.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (fixtureId) => {
            try {
              const response = await fetch(`${functionsUrl}/getCachedSGPs?fixture_id=${fixtureId}`);
              const data = await response.json();
              
              if (data.success && data.sgps) {
                // Store lightweight status (just stats, not full SGP data)
                setCachedSGPs(prev => ({
                  ...prev,
                  [fixtureId]: {
                    stats: data.stats,
                    generated_at: data.generated_at,
                    expires_at: data.expires_at
                  }
                }));
              }
            } catch (err) {
              // Silently fail for individual fixtures
              console.debug(`[SGPCache] No SGPs for ${fixtureId}`);
            }
          })
        );
      }
      
      console.log('[SGPCache] Finished fetching SGP status');
    } catch (err) {
      console.error('Failed to fetch cached SGPs:', err);
    }
  };
  
  // Fetch cached SGPs for a specific fixture
  const fetchCachedSGPs = async (fixtureId) => {
    try {
      const response = await fetch(`${functionsUrl}/getCachedSGPs?fixture_id=${fixtureId}`);
      const data = await response.json();
      
      if (data.success && data.sgps) {
        console.log(`[SGPCache] Fetched SGPs for ${fixtureId}:`, {
          sgpsType: typeof data.sgps,
          sgpsKeys: Object.keys(data.sgps || {}),
          stats: data.stats
        });
        
        setCachedSGPs(prev => ({
          ...prev,
          [fixtureId]: {
            sgps: data.sgps,
            stats: data.stats,
            generated_at: data.generated_at,
            expires_at: data.expires_at
          }
        }));
        return data;
      }
      return null;
    } catch (err) {
      console.error(`Failed to fetch SGPs for ${fixtureId}:`, err);
      return null;
    }
  };
  
  // Generate SGPs for a fixture
  const generateSGPs = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    
    setGenerating(prev => ({ ...prev, [fixtureId]: true }));
    setGenerationLogs(prev => ({ ...prev, [fixtureId]: '🚀 Starting SGP generation...\n' }));
    
    try {
      const cachedOdds = fixture.featured_markets || fixture.markets || null;
      
      setGenerationLogs(prev => ({ 
        ...prev, 
        [fixtureId]: prev[fixtureId] + `📊 Analyzing ${fixture.total_markets} markets with ${fixture.total_odds_available} odds\n`
      }));
      
      const response = await fetch(`${functionsUrl}/generateSGPs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fixture_id: fixtureId,
          cached_odds: cachedOdds,
          options: {
            legCounts: [2, 3],
            topNPerLeg: 25,
            sportsbooks: ['DraftKings', 'Caesars', 'BetMGM', 'FanDuel', 'BetRivers', 'Unibet'],
            minEV: 0,
            smartCombosOnly: true,
            cache: true
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log(`[SGPCache] Generated SGPs for ${fixtureId}:`, {
          sgpsType: typeof result.sgps,
          sgpsKeys: Object.keys(result.sgps || {}),
          stats: result.stats
        });
        
        setGenerationLogs(prev => ({ 
          ...prev, 
          [fixtureId]: prev[fixtureId] + `✅ Generated ${result.stats.total_sgps} SGPs!\n`
        }));
        
        // Store in cache
        setCachedSGPs(prev => ({
          ...prev,
          [fixtureId]: {
            sgps: result.sgps,
            stats: result.stats,
            generated_at: new Date().toISOString()
          }
        }));
        
        // Auto-expand to show results
        setExpandedFixtures(prev => ({ ...prev, [fixtureId]: true }));
      } else {
        const errorMsg = result.error || 'Unknown error';
        let displayMsg = errorMsg;
        
        // Make error messages more user-friendly
        if (errorMsg.includes('Insufficient markets')) {
          displayMsg = '❌ Not enough SGP-friendly markets (need 5+ player/team props)\n   Try a fixture with more markets (NBA/NFL games work best)';
        }
        
        setGenerationLogs(prev => ({ 
          ...prev, 
          [fixtureId]: prev[fixtureId] + `${displayMsg}\n`
        }));
      }
    } catch (err) {
      setGenerationLogs(prev => ({ 
        ...prev, 
        [fixtureId]: prev[fixtureId] + `❌ Error: ${err.message}\n`
      }));
    } finally {
      setGenerating(prev => ({ ...prev, [fixtureId]: false }));
    }
  };
  
  
  // ⭐ NEW: Server-side batch generation (faster than client-side)
  // All sports that have active SGP automation (soccer excluded — no parlay props yet)
  const ALL_SGP_SPORTS    = ['basketball', 'football', 'hockey', 'baseball', 'soccer', 'tennis', 'combat', 'golf', 'rugby', 'cricket', 'motorsports', 'esports'];
  const MULTILEG_SPORTS   = ['basketball', 'baseball', 'hockey', 'football'];

  // Fires refreshAllSGPsSports for every active sport in parallel, mirroring what the
  // scheduled Cloud Functions do each half-hour. Each call gets its own Cloud Function
  // instance (full 540s budget) so sports never block each other.
  const triggerFullLeagueRefresh = async () => {
    try {
      setGeneratingAll(true);
      setBulkProgress({
        processed: 0,
        total: ALL_SGP_SPORTS.length + MULTILEG_SPORTS.length,
        successful: 0,
        failed: 0,
        totalSGPs: 0,
        status: 'running',
        currentFixture: `Firing ${ALL_SGP_SPORTS.length} 2-leg + ${MULTILEG_SPORTS.length} multi-leg jobs in parallel…`,
      });

      const base = `${functionsUrl}/refreshAllSGPsSports?bypassFreshness=true`;

      // 2-leg pass — all sports simultaneously
      const twoLegPromises = ALL_SGP_SPORTS.map(sport =>
        fetch(`${base}&sport=${sport}`)
          .then(r => ({ sport, ok: r.ok }))
          .catch(err => ({ sport, ok: false, err: err.message }))
      );

      // Multi-leg append pass — fires in parallel alongside 2-leg (separate CF instances)
      const multiLegPromises = MULTILEG_SPORTS.map(sport =>
        fetch(`${base}&sport=${sport}&appendMode=true`)
          .then(r => ({ sport, ok: r.ok, multileg: true }))
          .catch(err => ({ sport, ok: false, err: err.message, multileg: true }))
      );

      // Poll aggregate stats every 30s while jobs run
      const statsInterval = setInterval(async () => {
        try {
          const r = await fetch(`${functionsUrl}/getSGPCacheAggregateStats`);
          const d = await r.json();
          const total = d?.aggregate?.total_sgps_cached || d?.total_sgps || 0;
          setBulkProgress(prev => prev ? { ...prev, totalSGPs: total } : prev);
        } catch (_) {}
      }, 30_000);

      // Wait for all jobs (each can take up to ~5 min — browser keeps connection open)
      const all = await Promise.allSettled([...twoLegPromises, ...multiLegPromises]);
      clearInterval(statsInterval);

      const succeeded = all.filter(r => r.status === 'fulfilled' && r.value?.ok).length;
      const failed    = all.length - succeeded;

      // Final stats refresh
      try {
        const r  = await fetch(`${functionsUrl}/getSGPCacheAggregateStats`);
        const d  = await r.json();
        const total = d?.aggregate?.total_sgps_cached || d?.total_sgps || 0;
        setBulkProgress({ processed: all.length, total: all.length, successful: succeeded, failed, totalSGPs: total, status: 'complete' });
      } catch (_) {
        setBulkProgress({ processed: all.length, total: all.length, successful: succeeded, failed, totalSGPs: 0, status: 'complete' });
      }

      fetchSGPCacheStats();
      fetchFixtures();
      console.log(`[SGPCache] Full league refresh done — ${succeeded} jobs succeeded, ${failed} failed`);
    } catch (err) {
      console.error('[SGPCache] Full league refresh error:', err);
      setGeneratingAll(false);
      setBulkProgress(null);
    } finally {
      setGeneratingAll(false);
    }
  };
  
  // Toggle fixture expansion
  const toggleFixture = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const isExpanding = !expandedFixtures[fixtureId];
    
    setExpandedFixtures(prev => ({ ...prev, [fixtureId]: isExpanding }));
    
    // If expanding and no cached SGPs yet, try to fetch
    if (isExpanding && !cachedSGPs[fixtureId]) {
      await fetchCachedSGPs(fixtureId);
    }
  };
  
  // Toggle SGP expansion (show all legs)
  const toggleSGP = (fixtureId, legCount, sgpIndex) => {
    const key = `${fixtureId}_${legCount}_${sgpIndex}`;
    setExpandedSGPs(prev => ({ ...prev, [key]: !prev[key] }));
  };
  return {
    fixtures,
    cachedSGPs,
    selectedLeagues,
    setSelectedLeagues,
    searchQuery,
    setSearchQuery,
    loading,
    generating,
    generatingAll,
    expandedFixtures,
    expandedSGPs,
    generationLogs,
    bulkProgress,
    collapsedSections,
    setCollapsedSections,
    countdown,
    sgpCacheStats,
    sgpHealth,
    sgpHealthExpanded,
    setSgpHealthExpanded,
    leagues,
    fetchSGPCacheStats,
    fetchSgpHealth,
    fetchFixtures,
    fetchAllCachedSGPs,
    generateSGPs,
    triggerFullLeagueRefresh,
    toggleFixture,
    toggleSGP,
    ALL_SGP_SPORTS,
    MULTILEG_SPORTS
  };
}
